'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { locales, localeNames, type Locale } from '@/lib/i18n';
import { ROUTES } from '@/lib/routes';
import { getLocalizedSlug, getBaseSlug } from '@/lib/blog-slugs';
import { FlagIcon } from './FlagIcon';
import styles from './LanguageSwitcher.module.css';

/**
 * Language Switcher Component
 * Allows users to change locale while preserving current path
 * Opens on hover like navigation menu items
 */
export function LanguageSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const params = useParams();
  const pathname = usePathname();
  const router = useRouter();
  
  // Determine current locale: from params, or detect from pathname
  let currentLocale: Locale = 'en';
  if (params?.locale && locales.includes(params.locale as any)) {
    currentLocale = params.locale as Locale;
  } else if (pathname) {
    const match = locales.find((l) => l !== 'en' && (pathname === `/${l}` || pathname.startsWith(`/${l}/`)));
    if (match) currentLocale = match as Locale;
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Helper functions for hover behavior
  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    clearCloseTimeout();
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200); // 200ms delay before closing (same as navigation menu)
  };

  // Toggle function for mobile/touch devices - allows opening and closing with clicks
  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  // Get path without locale prefix (handle any non-default locale, not /en/ since it's at root)
  const rawPath = pathname || '/';
  const nonDefault = locales.filter((l) => l !== 'en');
  const localePrefixRegex = nonDefault.length ? new RegExp(`^/(${nonDefault.join('|')})(?=/|$)`) : /^$/;
  const stripped = rawPath.replace(localePrefixRegex, '');
  const pathWithoutLocale = stripped === '' ? '/' : stripped;

  // Map locale-specific URLs using centralized ROUTES
  const getLocaleSpecificPath = (targetLocale: Locale, currentPath: string): string => {
    // Get full pathname including locale prefix for accurate matching
    const fullPath = pathname || '/';
    
    // Check legal pages first (using full path to match correctly)
    const isPrivacyPage = locales.some(
      (l) => fullPath === ROUTES.privacy[l] || fullPath === `/${l}${ROUTES.privacy[l]}`,
    );
    if (isPrivacyPage) return ROUTES.privacy[targetLocale];

    const isTermsPage = locales.some(
      (l) => fullPath === ROUTES.terms[l] || fullPath === `/${l}${ROUTES.terms[l]}`,
    );
    if (isTermsPage) return ROUTES.terms[targetLocale];

    const isCookiesPage = locales.some(
      (l) => fullPath === ROUTES.cookies[l] || fullPath === `/${l}${ROUTES.cookies[l]}`,
    );
    if (isCookiesPage) return ROUTES.cookies[targetLocale];

    // Check integrations pages
    const isIntegrationsPage = Object.values(ROUTES.integrations).some(path => currentPath === path);
    if (isIntegrationsPage) {
      return ROUTES.integrations[targetLocale];
    }

    // Check if it's a specific integration detail page
    // Return path WITHOUT locale prefix - href construction will add it
    if (
      currentPath.startsWith('/integrations/') ||
      currentPath.startsWith('/integraties/') ||
      currentPath.startsWith('/integrationen/') ||
      currentPath.startsWith('/integrationer/')
    ) {
      const slug = currentPath.split('/').pop() || '';
      return `${ROUTES.integrations[targetLocale]}/${slug}`;
    }

    // Check if it's a partner detail page
    // Return path WITHOUT locale prefix - href construction will add it
    if (currentPath.startsWith('/partners/')) {
      const slug = currentPath.split('/').pop() || '';
      return `/partners/${slug}`;
    }

    // Check industries/branches/branchen overview pages
    const isIndustriesPage = Object.values(ROUTES.industries).some(path => currentPath === path);
    if (isIndustriesPage) {
      return ROUTES.industries[targetLocale];
    }

    // Check if it's a specific industry detail page - handle cross-language mapping
    if (
      currentPath.startsWith('/industries/') ||
      currentPath.startsWith('/branches/') ||
      currentPath.startsWith('/branchen/') ||
      currentPath.startsWith('/branscher/')
    ) {
      // Extract the slug from current path
      const slug = currentPath.split('/').pop() || '';

      // Industry/branch slug mapping between languages
      type IndustryEntry = { en: string; nl: string; de: string; sv: string };
      const industryMap: Record<string, IndustryEntry> = {
        // Map all possible slugs across all languages to their correct equivalents
        'restaurants': { en: 'restaurants', nl: 'restaurants', de: 'restaurants', sv: 'restauranger' },
        'restauranger': { en: 'restaurants', nl: 'restaurants', de: 'restaurants', sv: 'restauranger' },
        'hotels': { en: 'hotels', nl: 'hotels', de: 'hotels', sv: 'hotell' },
        'hotell': { en: 'hotels', nl: 'hotels', de: 'hotels', sv: 'hotell' },
        'cafes-bars': { en: 'cafes-bars', nl: 'cafes-bars', de: 'cafes-bars', sv: 'kafeer-och-barer' },
        'kafeer-och-barer': { en: 'cafes-bars', nl: 'cafes-bars', de: 'cafes-bars', sv: 'kafeer-och-barer' },
        'beach-clubs': { en: 'beach-clubs', nl: 'strandtenten', de: 'strandclubs', sv: 'strandbarer' },
        'strandtenten': { en: 'beach-clubs', nl: 'strandtenten', de: 'strandclubs', sv: 'strandbarer' },
        'strandclubs': { en: 'beach-clubs', nl: 'strandtenten', de: 'strandclubs', sv: 'strandbarer' },
        'strandbarer': { en: 'beach-clubs', nl: 'strandtenten', de: 'strandclubs', sv: 'strandbarer' },
        'events-festivals': { en: 'events-festivals', nl: 'events-festivals', de: 'events-festivals', sv: 'evenemang-och-festivaler' },
        'evenemang-och-festivaler': { en: 'events-festivals', nl: 'events-festivals', de: 'events-festivals', sv: 'evenemang-och-festivaler' },
        'bowling-centers': { en: 'bowling-centers', nl: 'bowlingcentra', de: 'bowlingcenter', sv: 'bowlinghallar' },
        'bowlingcentra': { en: 'bowling-centers', nl: 'bowlingcentra', de: 'bowlingcenter', sv: 'bowlinghallar' },
        'bowlingcenter': { en: 'bowling-centers', nl: 'bowlingcentra', de: 'bowlingcenter', sv: 'bowlinghallar' },
        'bowlinghallar': { en: 'bowling-centers', nl: 'bowlingcentra', de: 'bowlingcenter', sv: 'bowlinghallar' },
        'zoos': { en: 'zoos', nl: 'dierentuinen', de: 'zoos', sv: 'djurparker' },
        'dierentuinen': { en: 'zoos', nl: 'dierentuinen', de: 'zoos', sv: 'djurparker' },
        'djurparker': { en: 'zoos', nl: 'dierentuinen', de: 'zoos', sv: 'djurparker' },
        'golf-courses': { en: 'golf-courses', nl: 'golfbanen', de: 'golfbahnen', sv: 'golfklubbar' },
        'golfbanen': { en: 'golf-courses', nl: 'golfbanen', de: 'golfbahnen', sv: 'golfklubbar' },
        'golfbahnen': { en: 'golf-courses', nl: 'golfbanen', de: 'golfbahnen', sv: 'golfklubbar' },
        'golfklubbar': { en: 'golf-courses', nl: 'golfbanen', de: 'golfbahnen', sv: 'golfklubbar' },
        'all-you-can-eat': { en: 'all-you-can-eat', nl: 'all-you-can-eat', de: 'all-you-can-eat', sv: 'all-you-can-eat' },
        'airports': { en: 'airports', nl: 'luchthavens', de: 'flughaefen', sv: 'flygplatser' },
        'luchthavens': { en: 'airports', nl: 'luchthavens', de: 'flughaefen', sv: 'flygplatser' },
        'flughaefen': { en: 'airports', nl: 'luchthavens', de: 'flughaefen', sv: 'flygplatser' },
        'flygplatser': { en: 'airports', nl: 'luchthavens', de: 'flughaefen', sv: 'flygplatser' },
        'amusement-parks': { en: 'amusement-parks', nl: 'pretparken', de: 'freizeitparks', sv: 'nojesparker' },
        'pretparken': { en: 'amusement-parks', nl: 'pretparken', de: 'freizeitparks', sv: 'nojesparker' },
        'freizeitparks': { en: 'amusement-parks', nl: 'pretparken', de: 'freizeitparks', sv: 'nojesparker' },
        'nojesparker': { en: 'amusement-parks', nl: 'pretparken', de: 'freizeitparks', sv: 'nojesparker' },
        'bakeries': { en: 'bakeries', nl: 'bakkerijen', de: 'baeckereien', sv: 'bagerier' },
        'bakkerijen': { en: 'bakeries', nl: 'bakkerijen', de: 'baeckereien', sv: 'bagerier' },
        'baeckereien': { en: 'bakeries', nl: 'bakkerijen', de: 'baeckereien', sv: 'bagerier' },
        'bagerier': { en: 'bakeries', nl: 'bakkerijen', de: 'baeckereien', sv: 'bagerier' },
        'canteens': { en: 'canteens', nl: 'kantines', de: 'kantinen', sv: 'personalmatsalar' },
        'kantines': { en: 'canteens', nl: 'kantines', de: 'kantinen', sv: 'personalmatsalar' },
        'kantinen': { en: 'canteens', nl: 'kantines', de: 'kantinen', sv: 'personalmatsalar' },
        'personalmatsalar': { en: 'canteens', nl: 'kantines', de: 'kantinen', sv: 'personalmatsalar' },
        'casinos': { en: 'casinos', nl: 'casinos', de: 'kasinos', sv: 'kasinon' },
        'kasinos': { en: 'casinos', nl: 'casinos', de: 'kasinos', sv: 'kasinon' },
        'kasinon': { en: 'casinos', nl: 'casinos', de: 'kasinos', sv: 'kasinon' },
        'catering': { en: 'catering', nl: 'catering', de: 'catering', sv: 'catering' },
        'concert-halls': { en: 'concert-halls', nl: 'concertzalen', de: 'konzertsaele', sv: 'konsertlokaler' },
        'concertzalen': { en: 'concert-halls', nl: 'concertzalen', de: 'konzertsaele', sv: 'konsertlokaler' },
        'konzertsaele': { en: 'concert-halls', nl: 'concertzalen', de: 'konzertsaele', sv: 'konsertlokaler' },
        'konsertlokaler': { en: 'concert-halls', nl: 'concertzalen', de: 'konzertsaele', sv: 'konsertlokaler' },
        'congress-centers': { en: 'congress-centers', nl: 'congressen', de: 'kongresse', sv: 'kongresscenter' },
        'congressen': { en: 'congress-centers', nl: 'congressen', de: 'kongresse', sv: 'kongresscenter' },
        'kongresse': { en: 'congress-centers', nl: 'congressen', de: 'kongresse', sv: 'kongresscenter' },
        'kongresscenter': { en: 'congress-centers', nl: 'congressen', de: 'kongresse', sv: 'kongresscenter' },
        'cruise-ships': { en: 'cruise-ships', nl: 'cruiseschepen', de: 'kreuzfahrtschiffe', sv: 'kryssningsfartyg' },
        'cruiseschepen': { en: 'cruise-ships', nl: 'cruiseschepen', de: 'kreuzfahrtschiffe', sv: 'kryssningsfartyg' },
        'kreuzfahrtschiffe': { en: 'cruise-ships', nl: 'cruiseschepen', de: 'kreuzfahrtschiffe', sv: 'kryssningsfartyg' },
        'kryssningsfartyg': { en: 'cruise-ships', nl: 'cruiseschepen', de: 'kreuzfahrtschiffe', sv: 'kryssningsfartyg' },
        'expos': { en: 'expos', nl: 'beurzen', de: 'messen', sv: 'massor' },
        'beurzen': { en: 'expos', nl: 'beurzen', de: 'messen', sv: 'massor' },
        'messen': { en: 'expos', nl: 'beurzen', de: 'messen', sv: 'massor' },
        'massor': { en: 'expos', nl: 'beurzen', de: 'messen', sv: 'massor' },
        'fast-food': { en: 'fast-food', nl: 'fastfood', de: 'fastfood', sv: 'snabbmat' },
        'fastfood': { en: 'fast-food', nl: 'fastfood', de: 'fastfood', sv: 'snabbmat' },
        'snabbmat': { en: 'fast-food', nl: 'fastfood', de: 'fastfood', sv: 'snabbmat' },
        'ferries': { en: 'ferries', nl: 'ferries', de: 'faehren', sv: 'farjor' },
        'faehren': { en: 'ferries', nl: 'ferries', de: 'faehren', sv: 'farjor' },
        'farjor': { en: 'ferries', nl: 'ferries', de: 'faehren', sv: 'farjor' },
        'food-halls': { en: 'food-halls', nl: 'foodhalls', de: 'food-courts', sv: 'food-courts' },
        'foodhalls': { en: 'food-halls', nl: 'foodhalls', de: 'food-courts', sv: 'food-courts' },
        'food-courts': { en: 'food-halls', nl: 'foodhalls', de: 'food-courts', sv: 'food-courts' },
        'food-trucks': { en: 'food-trucks', nl: 'foodtrucks', de: 'foodtrucks', sv: 'foodtrucks' },
        'foodtrucks': { en: 'food-trucks', nl: 'foodtrucks', de: 'foodtrucks', sv: 'foodtrucks' },
        'holiday-parks': { en: 'holiday-parks', nl: 'vakantieparken', de: 'ferienparks', sv: 'semesterparker' },
        'vakantieparken': { en: 'holiday-parks', nl: 'vakantieparken', de: 'ferienparks', sv: 'semesterparker' },
        'ferienparks': { en: 'holiday-parks', nl: 'vakantieparken', de: 'ferienparks', sv: 'semesterparker' },
        'semesterparker': { en: 'holiday-parks', nl: 'vakantieparken', de: 'ferienparks', sv: 'semesterparker' },
        'hospitals': { en: 'hospitals', nl: 'ziekenhuizen', de: 'krankenhaeuser', sv: 'sjukhus' },
        'ziekenhuizen': { en: 'hospitals', nl: 'ziekenhuizen', de: 'krankenhaeuser', sv: 'sjukhus' },
        'krankenhaeuser': { en: 'hospitals', nl: 'ziekenhuizen', de: 'krankenhaeuser', sv: 'sjukhus' },
        'sjukhus': { en: 'hospitals', nl: 'ziekenhuizen', de: 'krankenhaeuser', sv: 'sjukhus' },
        'ice-cream-parlors': { en: 'ice-cream-parlors', nl: 'ijssalons', de: 'eisdielen', sv: 'glassbarer' },
        'ijssalons': { en: 'ice-cream-parlors', nl: 'ijssalons', de: 'eisdielen', sv: 'glassbarer' },
        'eisdielen': { en: 'ice-cream-parlors', nl: 'ijssalons', de: 'eisdielen', sv: 'glassbarer' },
        'glassbarer': { en: 'ice-cream-parlors', nl: 'ijssalons', de: 'eisdielen', sv: 'glassbarer' },
        'lunchrooms': { en: 'lunchrooms', nl: 'lunchrooms', de: 'lunchrooms', sv: 'lunchrestauranger' },
        'lunchrestauranger': { en: 'lunchrooms', nl: 'lunchrooms', de: 'lunchrooms', sv: 'lunchrestauranger' },
        'museums': { en: 'museums', nl: 'musea', de: 'museen', sv: 'museer' },
        'musea': { en: 'museums', nl: 'musea', de: 'museen', sv: 'museer' },
        'museen': { en: 'museums', nl: 'musea', de: 'museen', sv: 'museer' },
        'museer': { en: 'museums', nl: 'musea', de: 'museen', sv: 'museer' },
        'spa-wellness': { en: 'spa-wellness', nl: 'spa-wellness', de: 'spa-wellness', sv: 'spa-och-wellness' },
        'spa-och-wellness': { en: 'spa-wellness', nl: 'spa-wellness', de: 'spa-wellness', sv: 'spa-och-wellness' },
        'stadiums': { en: 'stadiums', nl: 'stadions', de: 'stadien', sv: 'arenor' },
        'stadions': { en: 'stadiums', nl: 'stadions', de: 'stadien', sv: 'arenor' },
        'stadien': { en: 'stadiums', nl: 'stadions', de: 'stadien', sv: 'arenor' },
        'arenor': { en: 'stadiums', nl: 'stadions', de: 'stadien', sv: 'arenor' },
        'universities': { en: 'universities', nl: 'universiteiten', de: 'universitaeten', sv: 'universitet' },
        'universiteiten': { en: 'universities', nl: 'universiteiten', de: 'universitaeten', sv: 'universitet' },
        'universitaeten': { en: 'universities', nl: 'universiteiten', de: 'universitaeten', sv: 'universitet' },
        'universitet': { en: 'universities', nl: 'universiteiten', de: 'universitaeten', sv: 'universitet' },
      };

      // Get the correct slug for target locale
      const mapping = industryMap[slug];
      if (mapping) {
        const targetSlug = mapping[targetLocale] ?? mapping.en;
        // Use correct base path for target locale (industries/branches/branchen/branscher)
        return `${ROUTES.industries[targetLocale]}/${targetSlug}`;
      }
      // Fallback for unknown slugs: keep slug identical
      return `${ROUTES.industries[targetLocale]}/${slug}`;
    }

    // Check if it's a blog post page - handle slug translation
    // Detect any locale's blog prefix (en/nl/de = /blog, sv = /blogg)
    const blogPrefixRegex = new RegExp(`^(${locales.map((l) => ROUTES.blog[l as Locale]).join('|')})/`);
    const blogMatch = currentPath.match(blogPrefixRegex);
    if (blogMatch) {
      const currentSlug = currentPath.slice(blogMatch[0].length);
      const baseSlug = getBaseSlug(currentSlug);
      const finalSlug = baseSlug ? getLocalizedSlug(baseSlug, targetLocale) : currentSlug;
      return `${ROUTES.blog[targetLocale]}/${finalSlug}`;
    }

    // Check all solution pages
    const isKioskPage = Object.values(ROUTES.kiosk).some(path => currentPath === path);
    if (isKioskPage) return ROUTES.kiosk[targetLocale];

    const isQRPage = Object.values(ROUTES.qr).some(path => currentPath === path);
    if (isQRPage) return ROUTES.qr[targetLocale];

    const isTabletPage = Object.values(ROUTES.tablet).some(path => currentPath === path);
    if (isTabletPage) return ROUTES.tablet[targetLocale];

    const isWebshopPage = Object.values(ROUTES.webshop).some(path => currentPath === path);
    if (isWebshopPage) return ROUTES.webshop[targetLocale];

    const isPosGuidePage = Object.values(ROUTES.posGuide).some(path => currentPath === path);
    if (isPosGuidePage) return ROUTES.posGuide[targetLocale];

    const isStaffShortagePage = Object.values(ROUTES.staffShortage).some(path => currentPath === path);
    if (isStaffShortagePage) return ROUTES.staffShortage[targetLocale];

    // Check core pages
    const isDemoPage = Object.values(ROUTES.demo).some(path => currentPath === path);
    if (isDemoPage) return ROUTES.demo[targetLocale];

    const isThankYouPage = Object.values(ROUTES.thankYou).some(path => currentPath === path);
    if (isThankYouPage) return ROUTES.thankYou[targetLocale];

    const isLeadRegistrationPage = Object.values(ROUTES.leadRegistration).some(path => currentPath === path);
    if (isLeadRegistrationPage) return ROUTES.leadRegistration[targetLocale];

    const isLeadRegisteredPage = Object.values(ROUTES.leadRegistered).some(path => currentPath === path);
    if (isLeadRegisteredPage) return ROUTES.leadRegistered[targetLocale];

    // For other pages, return the same path
    return currentPath;
  };

  return (
    <div 
      className={styles.container} 
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className={styles.trigger}
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <FlagIcon locale={currentLocale} className={styles.flag} />
        <span className={styles.currentLocale}>
          {currentLocale.toUpperCase()}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className={`${styles.icon} ${isOpen ? styles.iconOpen : ''}`}
        >
          <path
            d="M2 4L6 8L10 4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.dropdown} role="menu">
          {locales.map((locale) => {
            const localePath = getLocaleSpecificPath(locale, pathWithoutLocale);
            // English (default) goes to root, other languages get prefix
            const href = locale === 'en' 
              ? (localePath === '/' ? '/' : localePath)
              : `/${locale}${localePath === '/' ? '' : localePath}`;
            const isCurrent = locale === currentLocale;

            const handleLocaleChange = (e: React.MouseEvent) => {
              e.preventDefault();
              setIsOpen(false);
              
              // Set cookie and force a hard navigation to ensure middleware reads it
              document.cookie = `preferred_locale=${locale}; path=/; max-age=31536000; SameSite=Lax`;
              
              // Use window.location for hard navigation (ensures cookie is sent)
              // Small delay to ensure cookie is set
              setTimeout(() => {
                window.location.href = href;
              }, 50);
            };

            return (
              <button
                key={locale}
                type="button"
                className={`${styles.option} ${isCurrent ? styles.active : ''}`}
                role="menuitem"
                aria-current={isCurrent ? 'true' : undefined}
                onClick={handleLocaleChange}
              >
                <FlagIcon locale={locale} className={styles.flag} />
                <span className={styles.localeName}>{localeNames[locale]}</span>
                <span className={styles.localeCode}>{locale.toUpperCase()}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
